import { AvailabilityEntry } from '../../api/types';

export const addDays = (date: Date, days: number) =>
  new Date(date.getTime() + days * 24 * 60 * 60 * 1000);

export const addMonths = (date: Date, months: number) => {
  const d = new Date(date); // clone to avoid mutating the original date
  d.setMonth(d.getMonth() + months);
  return d;
};

const formatDate = (date: Date) => {
  const day = date.getDate();
  const formattedDay = day < 10 ? `0${day}` : day;
  const month = date.getMonth() + 1;
  const formattedMonth = month < 10 ? `0${month}` : month;
  const year = date.getFullYear();
  return `${year}-${formattedMonth}-${formattedDay}`; // YYYY-MM-DD
};

export const formatTime = (isoString: string, timeZone: string | undefined) => {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  });
};

export const formatSelectedDate = (date: Date) =>
  date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
  });

export const formatMonthYear = (date: Date) =>
  date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

export const formatWeekRange = (startDate: Date) => {
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  return `${startDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })} - ${endDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })}`;
};

// Given the array of all the availability entries,
// this function returns the time slots for the specified date.
// If minDate is provided, it will only return times for dates after minDate.
export const getDateAvailability = (
  availability: AvailabilityEntry[],
  date: Date,
  minDate: Date | undefined = undefined
) => {
  const dateString = formatDate(date);
  const minDateString = minDate ? formatDate(minDate) : undefined;
  const userTimezoneMinDate = minDateString
    ? convertLocalBrowserDateToUserDate(new Date(minDateString))
    : undefined;

  const availableTimesForDate = availability.find((entry) =>
    userTimezoneMinDate
      ? date.getTime() >= userTimezoneMinDate.getTime() &&
        entry.date === dateString
      : entry.date === dateString
  );

  if (!availableTimesForDate) return [];

  return availableTimesForDate.times;
};

export const getTimeZoneShortName = (timeZone: string | undefined) => {
  if (!timeZone) return null;

  const opts: Intl.DateTimeFormatOptions = {
    timeZoneName: 'short',
    timeZone,
  };
  return Intl.DateTimeFormat('en-EN', opts).format(Date.now()).split(',')[1];
};

// "Eastern Daylight Time" plus "EDT" becomes "Eastern time (ET)". The season is
// dropped on purpose: a slot months away should not carry today's daylight
// saving in its label.
export const formatTimeZoneLabel = (timeZone: string | undefined) => {
  if (!timeZone) return null;

  const longName = Intl.DateTimeFormat('en-US', {
    timeZoneName: 'long',
    timeZone,
  })
    .format(Date.now())
    .split(', ')
    .pop();
  const shortName = getTimeZoneShortName(timeZone)?.trim();

  if (!longName || !shortName) return null;

  const region = longName
    .replace(/\s*(Daylight|Standard|Summer)\s*/i, ' ')
    .replace(/\s*Time$/i, '')
    .trim();
  const abbreviation = shortName.replace(/(?<=.)[DS](?=T$)/, '');

  return `${region} time (${abbreviation})`;
};

// The month of `date`, padded with nulls so it starts on a Monday and fills
// whole weeks. Nulls are the days belonging to the neighbouring months.
export const getMonthGrid = (date: Date): (Date | null)[] => {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const dayCount = new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0
  ).getDate();
  const lead = (first.getDay() + 6) % 7;

  const cells: (Date | null)[] = Array.from({ length: lead }, () => null);
  for (let day = 1; day <= dayCount; day += 1) {
    cells.push(new Date(date.getFullYear(), date.getMonth(), day));
  }
  while (cells.length % 7 !== 0) cells.push(null);

  return cells;
};

export const getStartOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

// The same grid split into weeks, one row each.
export const getMonthWeeks = (date: Date): (Date | null)[][] => {
  const cells = getMonthGrid(date);
  const weeks: (Date | null)[][] = [];

  for (let index = 0; index < cells.length; index += 7) {
    weeks.push(cells.slice(index, index + 7));
  }

  return weeks;
};

// The day cells allow one day in the past, so that is the floor a week has to
// clear to be worth showing. Midnight keeps the floor still: a floor carrying
// the time of day drops yesterday halfway through today.
export const getEarliestSelectableDay = () => {
  const earliest = new Date();
  earliest.setDate(earliest.getDate() - 1);
  earliest.setHours(0, 0, 0, 0);
  return earliest;
};

export const isPastWeek = (week: (Date | null)[]) => {
  const earliest = getEarliestSelectableDay();
  return week.every((date) => !date || date < earliest);
};

// Whether the whole of the month after `date` sits past the booking window.
export const exceedMonthBound = (date: Date, maxWeeksCount: number) => {
  const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);
  const timeBound = addDays(new Date(), 7 * maxWeeksCount);
  return nextMonth.getTime() >= timeBound.getTime();
};

// Whether the month before `date` is entirely behind us.
export const precedesCurrentMonth = (date: Date) => {
  const today = new Date();
  return (
    date.getFullYear() < today.getFullYear() ||
    (date.getFullYear() === today.getFullYear() &&
      date.getMonth() <= today.getMonth())
  );
};

export const convertLocalBrowserDateToUserDate = (localBrowserDate: Date) => {
  const localTimezoneDate = new Date(
    localBrowserDate.toLocaleString('en-US', {
      timeZone: 'UTC',
    })
  );
  const diff = localBrowserDate.getTime() - localTimezoneDate.getTime();
  return new Date(localBrowserDate.getTime() - diff);
};
